import React from 'react';
import { HorseRead, UserHorseCollectionRead, Country } from '../types/api';
import { HorseCard } from './HorseCard';
import { Loader2 } from 'lucide-react';

interface ResultsGridProps {
  horses: HorseRead[];
  loading: boolean;
  error?: string;
  collectionHorseIds?: Set<string>;
  collectionData?: Map<string, UserHorseCollectionRead>;
  onCollectionChange?: (horseId: string, added: boolean, collectionData?: UserHorseCollectionRead) => void;
  onExpandClick?: (horseId: string) => void;
  hasMore?: boolean;
  onLoadMore?: () => void;
  loadingMore?: boolean;
  countries?: Country[];
}

export const ResultsGrid: React.FC<ResultsGridProps> = ({
  horses,
  loading,
  error,
  collectionHorseIds = new Set(),
  collectionData = new Map(),
  onCollectionChange,
  onExpandClick,
  hasMore,
  onLoadMore,
  loadingMore,
  countries = [],
}) => {
  if (loading && horses.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <Loader2 className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading horses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="text-red-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Horses</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (horses.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-center">
          <div className="text-gray-400 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Horses Found</h3>
          <p className="text-gray-600">
            Try adjusting your search criteria or filters to find more horses.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex justify-center relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <Loader2 className="animate-spin h-6 w-6 text-blue-500 opacity-70" />
        </div>
      )}
      <div className={`grid grid-cols-1 gap-4 w-full max-w-[320px] lg:max-w-[500px] transition-opacity duration-150 ${loading ? 'opacity-60' : 'opacity-100'}`}>
        {horses.map((horse) => (
          <HorseCard
            key={horse.unified_horse_key}
            horse={horse}
            isInCollection={collectionHorseIds.has(horse.unified_horse_key)}
            collectionData={collectionData.get(horse.unified_horse_key) || null}
            onCollectionChange={onCollectionChange}
            onExpandClick={onExpandClick}
            countries={countries}
          />
        ))}
        {(hasMore || loadingMore) && onLoadMore && (
          <div className="flex justify-center pt-2 pb-4">
            <button
              onClick={onLoadMore}
              disabled={loadingMore}
              className="inline-flex items-center justify-center gap-2 w-full px-8 py-3 text-base font-semibold text-[#132B64] bg-white border-2 border-[#132B64] rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  Loading...
                </>
              ) : (
                'Show more'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}; 